import { motion } from "framer-motion";
import { 
  Music, 
  Palette, 
  Trophy, 
  Users, 
  Lightbulb, 
  Globe,
  BookOpen,
  Theater,
  Mic,
  Camera
} from "lucide-react";

const clubs = [
  {
    title: "Science Club",
    icon: Lightbulb,
    description: "Explore the wonders of science through experiments, projects, and competitions.",
    activities: ["Science experiments", "Project exhibitions", "Quiz competitions", "Field trips"],
    image: "https://images.unsplash.com/photo-1532094349884-543bc11b234d?auto=format&fit=crop&w=600&q=80",
  },
  {
    title: "Literary Club",
    icon: BookOpen,
    description: "Develop language skills through debates, creative writing, and literary events.",
    activities: ["Debate competitions", "Creative writing", "Poetry recitation", "Book reviews"],
    image: "https://images.unsplash.com/photo-1521587760476-6c12a4b040da?auto=format&fit=crop&w=600&q=80",
  },
  {
    title: "Art & Craft Club",
    icon: Palette,
    description: "Express creativity through various art forms including painting, sculpture, and crafts.",
    activities: ["Painting sessions", "Craft workshops", "Art exhibitions", "Poster making"],
    image: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=600&q=80",
  },
  {
    title: "Music Club",
    icon: Music,
    description: "Learn and perform various forms of music including vocals and instruments.",
    activities: ["Vocal training", "Instrument classes", "Choir performances", "Music competitions"],
    image: "https://images.unsplash.com/photo-1511379938547-c1f69419868d?auto=format&fit=crop&w=600&q=80",
  },
  {
    title: "Drama Club",
    icon: Theater,
    description: "Develop acting skills and stage presence through theater and drama activities.",
    activities: ["Drama workshops", "Stage plays", "Skits", "Role-playing activities"],
    image: "https://images.unsplash.com/photo-1606761568499-6d2451b23c66?auto=format&fit=crop&w=600&q=80",
  },
  {
    title: "Photography Club",
    icon: Camera,
    description: "Learn the art of photography and visual storytelling.",
    activities: ["Photography basics", "Photo walks", "Editing workshops", "Photo exhibitions"],
    image: "https://images.unsplash.com/photo-1502920917128-1aa500764cbd?auto=format&fit=crop&w=600&q=80",
  },
];

const sportsPrograms = [
  { name: "Cricket", image: "https://images.unsplash.com/photo-1526676037777-05a232554f77?auto=format&fit=crop&w=400&q=80" },
  { name: "Football", image: "https://images.unsplash.com/photo-1461896836934-eba09b295a37?auto=format&fit=crop&w=400&q=80" },
  { name: "Basketball", image: "https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&w=400&q=80" },
  { name: "Athletics", image: "https://images.unsplash.com/photo-1461897104016-0b3b00b1ea56?auto=format&fit=crop&w=400&q=80" },
  { name: "Badminton", image: "https://images.unsplash.com/photo-1626224583764-f87db24ac4ea?auto=format&fit=crop&w=400&q=80" },
  { name: "Table Tennis", image: "https://images.unsplash.com/photo-1511067007398-7e4b90cfa4bc?auto=format&fit=crop&w=400&q=80" },
];

const culturalActivities = [
  "Annual Day Performances",
  "Independence Day Celebration",
  "Republic Day Program",
  "Teachers' Day",
  "Children's Day",
  "Festival Celebrations",
  "Inter-school Cultural Fests",
  "Talent Shows",
];

const competitions = [
  { title: "Science Olympiad", type: "Academic", frequency: "Annual" },
  { title: "Math Championship", type: "Academic", frequency: "Annual" },
  { title: "Spelling Bee", type: "Language", frequency: "Annual" },
  { title: "Essay Writing", type: "Language", frequency: "Quarterly" },
  { title: "Art Competition", type: "Creative", frequency: "Quarterly" },
  { title: "Sports Meet", type: "Sports", frequency: "Annual" },
  { title: "Quiz Competition", type: "Academic", frequency: "Monthly" },
  { title: "Singing Competition", type: "Cultural", frequency: "Annual" },
];

const StudentLife = () => {
  return (
    <div data-testid="student-life-page">
      {/* Page Header */}
      <section className="bg-primary py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              Student Life
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              Beyond academics - discover the vibrant life at ID4U School
            </p>
          </motion.div>
        </div>
      </section>

      {/* Clubs & Activities */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-secondary font-medium text-sm uppercase tracking-wider">
              Join a Community
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">
              Clubs & Activities
            </h2>
            <div className="section-divider mx-auto"></div>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Explore your interests and develop new skills through our diverse club offerings
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {clubs.map((club, index) => (
              <motion.div
                key={club.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100 hover:shadow-xl transition-all duration-300 group"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={club.image}
                    alt={club.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 w-12 h-12 bg-white rounded-xl flex items-center justify-center">
                    <club.icon className="text-primary" size={24} />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">
                    {club.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">{club.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {club.activities.map((activity) => (
                      <span key={activity} className="text-xs bg-slate-100 text-gray-600 px-2 py-1 rounded-full">
                        {activity}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sports Programs */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-secondary font-medium text-sm uppercase tracking-wider">
              Stay Active
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">
              Sports Programs
            </h2>
            <div className="section-divider mx-auto"></div>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Physical fitness is integral to our curriculum with professional coaching in various sports
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {sportsPrograms.map((sport, index) => (
              <motion.div
                key={sport.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative aspect-square rounded-xl overflow-hidden group cursor-pointer"
              >
                <img
                  src={sport.image}
                  alt={sport.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent flex items-end justify-center p-4">
                  <span className="text-white font-medium">{sport.name}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cultural Activities & Competitions */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Cultural Activities */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Theater className="text-primary" size={24} />
                </div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  Cultural Activities
                </h2>
              </div>
              <p className="text-gray-600 mb-6">
                We celebrate diversity and culture through various events throughout the year.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {culturalActivities.map((activity) => (
                  <div key={activity} className="flex items-center gap-2 bg-slate-50 p-3 rounded-lg">
                    <div className="w-2 h-2 bg-secondary rounded-full"></div>
                    <span className="text-sm text-gray-700">{activity}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Competitions */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-secondary/10 rounded-xl flex items-center justify-center">
                  <Trophy className="text-secondary" size={24} />
                </div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  Competitions
                </h2>
              </div>
              <p className="text-gray-600 mb-6">
                Regular competitions help students showcase their talents and build confidence.
              </p>
              <div className="bg-slate-50 rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-primary text-white">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium">Competition</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Frequency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {competitions.map((comp, index) => (
                      <tr key={comp.title} className={index % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{comp.title}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{comp.type}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{comp.frequency}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StudentLife;
